import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  ArrowLeft,
  Calendar,
  Users,
  Banknote,
  Edit,
  CheckCircle,
  Clock,
  AlertCircle,
  Target,
  Flag,
  Building,
  Mail,
  Phone,
  MapPin,
  Tag,
  Plus,
  X,
  AlertTriangle,
  MessageSquare,
  Save,
} from "lucide-react";
import { projectService } from "@/services/projectService";
import { formatCurrency } from "@/utils/formatters";
import { toast } from "react-hot-toast";

const ProjectDetail = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [makeTemplate, setMakeTemplate] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: "",
    progress_percentage: 0,
    notes: "",
  });
  const [newRisk, setNewRisk] = useState({
    description: "",
    impact: "medium",
    probability: "medium",
    mitigation: "",
    status: "open",
  });
  const [commentText, setCommentText] = useState("");
  const [projectComments, setProjectComments] = useState([]);

  // Load comments when project loads
  useEffect(() => {
    if (id) {
      projectService
        .getProjectComments(id)
        .then((res) => {
          const comments = res.data?.data?.comments || [];
          setProjectComments(Array.isArray(comments) ? comments : []);
        })
        .catch((err) => console.error("Failed to load comments:", err));
    }
  }, [id]);

  // Reload comments when modal opens
  useEffect(() => {
    if (showCommentModal && id) {
      projectService
        .getProjectComments(id)
        .then((res) => {
          const comments = res.data?.data?.comments || [];
          setProjectComments(Array.isArray(comments) ? comments : []);
        })
        .catch((err) => console.error("Failed to load comments:", err));
    }
  }, [showCommentModal, id]);

  const {
    data: project,
    isLoading,
    error,
  } = useQuery(["project", id], () => projectService.getProjectById(id), {
    select: (response) =>
      response?.data?.data?.project ||
      response?.data?.project ||
      response?.project,
    enabled: !!id,
  });

  const safeArray = (data) => {
    if (Array.isArray(data)) return data;
    if (!data) return [];
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const { data: tasks } = useQuery(
    ["project-tasks", id],
    () => projectService.getProjectTasks(id),
    {
      select: (response) =>
        response?.data?.data?.tasks || response?.data?.tasks || [],
      enabled: !!id,
    },
  );

  const addRiskMutation = useMutation(
    async (risk) => {
      const currentRisks = safeArray(project?.risks);
      const updatedRisks = [
        ...currentRisks,
        { ...risk, id: Date.now(), created_at: new Date().toISOString() },
      ];
      return projectService.updateProject(id, { risks: updatedRisks });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["project", id]);
        setShowRiskModal(false);
        setNewRisk({
          description: "",
          impact: "medium",
          probability: "medium",
          mitigation: "",
          status: "open",
        });
        toast.success("Risk/Issue registered");
      },
      onError: () => toast.error("Failed to register risk"),
    },
  );

  const removeRiskMutation = useMutation(
    async (index) => {
      const currentRisks = safeArray(project?.risks);
      const updatedRisks = currentRisks.filter((_, i) => i !== index);
      return projectService.updateProject(id, { risks: updatedRisks });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["project", id]);
        toast.success("Risk removed");
      },
      onError: () => toast.error("Failed to remove risk"),
    },
  );

  const updateStatusMutation = useMutation(
    (status) => projectService.updateProject(id, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["project", id]);
        toast.success("Status updated");
      },
      onError: () => toast.error("Failed to update status"),
    },
  );

  const updateProjectMutation = useMutation(
    (data) => projectService.updateProject(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["project", id]);
        queryClient.invalidateQueries(["template-projects"]);
        queryClient.invalidateQueries(["projects"]);
        setShowUpdateModal(false);
        setShowCompleteModal(false);
        toast.success("Project updated");
      },
      onError: () => toast.error("Failed to update project"),
    },
  );

  const handleCompleteProject = () => {
    updateProjectMutation.mutate({
      status: "completed",
      progress_percentage: 100,
      is_template: makeTemplate,
    });
  };

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    projectService
      .addProjectComment(id, { text: commentText })
      .then(() => {
        setProjectComments([
          ...projectComments,
          {
            id: Date.now(),
            text: commentText,
            timestamp: new Date().toLocaleString(),
          },
        ]);
        setCommentText("");
      })
      .catch((err) => console.error("Failed to add comment:", err));
  };

  const openUpdateModal = () => {
    setUpdateData({
      status: project?.status || "planning",
      progress_percentage: project?.progress_percentage || 0,
      notes: project?.notes || "",
    });
    setShowUpdateModal(true);
  };

  const statusConfig = {
    planning: {
      bg: "bg-slate-100",
      text: "text-slate-700",
      dot: "bg-slate-500",
    },
    active: {
      bg: "bg-emerald-100",
      text: "text-emerald-700",
      dot: "bg-emerald-500",
    },
    on_hold: {
      bg: "bg-amber-100",
      text: "text-amber-700",
      dot: "bg-amber-500",
    },
    completed: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
    cancelled: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
  };

  const priorityConfig = {
    low: { bg: "bg-gray-100", text: "text-gray-600" },
    medium: { bg: "bg-blue-100", text: "text-blue-700" },
    high: { bg: "bg-orange-100", text: "text-orange-700" },
    critical: { bg: "bg-red-100", text: "text-red-700" },
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Project Not Found
        </h3>
        <p className="text-gray-600 mb-6">
          {error?.message || "Unable to load project details."}
        </p>
        <Link
          to="/projects"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Projects
        </Link>
      </div>
    );
  }

  const p = project;
  const milestones = safeArray(p.milestones);
  const tags = safeArray(p.tags);
  const displayTasks = Array.isArray(tasks) ? tasks : [];
  const status = statusConfig[p.status] || statusConfig.planning;
  const priority = priorityConfig[p.priority] || priorityConfig.medium;

  const completedTasks = displayTasks.filter(
    (t) => t.status === "completed",
  ).length;
  const totalTasks = displayTasks.length;
  const progress =
    p.progress_percentage ||
    (totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/projects"
          className="inline-flex items-center text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Projects
        </Link>
        <div className="flex items-center gap-2">
          {p.status === "completed" && !p.is_template && (
            <button
              onClick={() =>
                updateProjectMutation.mutate({ is_template: true })
              }
              className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
            >
              Make Template
            </button>
          )}
          {p.status === "completed" && p.is_template && (
            <span className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
              ✓ Available as Template
            </span>
          )}
          {p.status === "active" && (
            <button
              onClick={() => setShowRiskModal(true)}
              className="inline-flex items-center px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
            >
              <AlertTriangle className="h-4 w-4 mr-2" /> Report Issue
            </button>
          )}
          <button
            onClick={openUpdateModal}
            className="inline-flex items-center px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50"
          >
            <MessageSquare className="h-4 w-4 mr-2" /> Update Progress
          </button>
          <button
            onClick={() => setShowCommentModal(true)}
            className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
          >
            <MessageSquare className="h-4 w-4 mr-2" /> Comments
          </button>
          <Link
            to={`/projects/${id}/edit`}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Edit className="h-4 w-4 mr-2" /> Edit Project
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl shadow-lg p-8 text-white">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                {p.code}
              </span>
              <select
                value={p.status}
                onChange={(e) => updateStatusMutation.mutate(e.target.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${status.bg} ${status.text}`}
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${priority.bg} ${priority.text}`}
              >
                {p.priority}
              </span>
            </div>
            <h1 className="text-3xl font-bold mb-2">{p.name}</h1>
            <p className="text-white/80 max-w-2xl">{p.description}</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{progress}%</div>
            <div className="text-white/70 text-sm">Complete</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="w-full bg-white/20 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {tags.map((tag, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-sm"
              >
                <Tag className="h-3 w-3" /> {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Duration
              </p>
              <p className="font-semibold text-gray-900">
                {new Date(p.start_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}{" "}
                -{" "}
                {new Date(p.end_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Banknote className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Budget
              </p>
              <p className="font-semibold text-gray-900">
                {formatCurrency(p.budget_allocated || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Milestones
              </p>
              <p className="font-semibold text-gray-900">
                {milestones.filter((m) => m.completed).length} /{" "}
                {milestones.length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Tasks
              </p>
              <p className="font-semibold text-gray-900">
                {completedTasks} / {totalTasks}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Milestones & Deliverables */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Flag className="h-5 w-5 text-primary-600" /> Milestones &
                Deliverables
              </h3>
            </div>
            <div className="p-6">
              {milestones.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No milestones defined
                </p>
              ) : (
                <div className="space-y-6">
                  {milestones.map((milestone, idx) => {
                    const deliverables = safeArray(milestone.deliverables);
                    const completedDel = deliverables.filter(
                      (d) => d.completed,
                    ).length;
                    return (
                      <div
                        key={idx}
                        className="relative pl-8 pb-6 border-l-2 border-gray-200 last:border-l-0 last:pb-0"
                      >
                        <div
                          className={`absolute left-[-9px] top-0 h-4 w-4 rounded-full border-2 border-white ${milestone.completed ? "bg-green-500" : "bg-gray-300"}`}
                        ></div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                {milestone.name}
                              </h4>
                              {milestone.description && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {milestone.description}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-medium text-gray-700">
                                {milestone.start_date
                                  ? new Date(
                                      milestone.start_date,
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : ""}
                                {milestone.start_date && milestone.end_date
                                  ? " - "
                                  : ""}
                                {milestone.end_date
                                  ? new Date(
                                      milestone.end_date,
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })
                                  : milestone.date
                                    ? new Date(
                                        milestone.date,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                    : ""}
                              </span>
                              {deliverables.length > 0 && (
                                <p className="text-xs text-gray-500">
                                  {completedDel}/{deliverables.length}{" "}
                                  deliverables
                                </p>
                              )}
                            </div>
                          </div>
                          {deliverables.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {deliverables.map((del, dIdx) => (
                                <div
                                  key={dIdx}
                                  className="flex items-center gap-3 p-2 bg-white rounded border"
                                >
                                  <div
                                    className={`h-5 w-5 rounded-full flex items-center justify-center ${del.completed ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"}`}
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                  </div>
                                  <span
                                    className={`flex-1 text-sm ${del.completed ? "text-gray-500 line-through" : "text-gray-700"}`}
                                  >
                                    {del.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {del.start_date
                                      ? new Date(
                                          del.start_date,
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })
                                      : ""}
                                    {del.start_date &&
                                    (del.end_date || del.due_date)
                                      ? " - "
                                      : ""}
                                    {del.end_date || del.due_date
                                      ? new Date(
                                          del.end_date || del.due_date,
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })
                                      : ""}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recent Tasks */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Recent Tasks
              </h3>
              <Link
                to={`/projects/${id}/tasks`}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View all →
              </Link>
            </div>
            <div className="divide-y">
              {displayTasks.slice(0, 5).map((task) => (
                <div
                  key={task.id}
                  className="p-4 flex items-center gap-4 hover:bg-gray-50"
                >
                  <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center ${task.status === "completed" ? "bg-green-100 text-green-600" : task.status === "in_progress" ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"}`}
                  >
                    {task.status === "completed" ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <Clock className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {task.title}
                    </p>
                    <p className="text-sm text-gray-500">
                      {task.AssignedTo?.User?.first_name}{" "}
                      {task.AssignedTo?.User?.last_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${task.status === "completed" ? "bg-green-100 text-green-700" : task.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}
                    >
                      {task.status?.replace("_", " ")}
                    </span>
                  </div>
                </div>
              ))}
              {displayTasks.length === 0 && (
                <p className="p-6 text-center text-gray-500">No tasks yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Project Manager */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Project Manager
            </h3>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-semibold text-lg">
                {p.Manager?.User?.first_name?.[0] || "P"}
                {p.Manager?.User?.last_name?.[0] || "M"}
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {p.Manager?.User?.first_name} {p.Manager?.User?.last_name}
                </p>
                <p className="text-sm text-gray-500">
                  {p.Manager?.position || "Project Manager"}
                </p>
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Team Members ({(p.ProjectAssignments || []).length})
            </h3>
            {(p.ProjectAssignments || []).length > 0 ? (
              <div className="space-y-3">
                {p.ProjectAssignments.map((assignment) => (
                  <div key={assignment.id} className="flex items-center gap-3">
                    <div className="h-9 w-9 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-medium text-sm">
                      {assignment.Employee?.User?.first_name?.[0]}
                      {assignment.Employee?.User?.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {assignment.Employee?.User?.first_name}{" "}
                        {assignment.Employee?.User?.last_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {assignment.role || assignment.Employee?.position}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No team members assigned</p>
            )}
          </div>

          {/* Manager Notes */}
          {p.notes && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" /> Manager Notes
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {p.notes}
              </p>
            </div>
          )}

          {/* Project Info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Project Details
            </h3>
            <dl className="space-y-4">
              {p.Department?.name && (
                <div className="flex items-start gap-3">
                  <Building className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <dt className="text-xs text-gray-500">Department</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {p.Department.name}
                    </dd>
                  </div>
                </div>
              )}
              {p.client_name && (
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <dt className="text-xs text-gray-500">Client</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {p.client_name}
                    </dd>
                  </div>
                </div>
              )}
              {p.client_email && (
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <dt className="text-xs text-gray-500">Email</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {p.client_email}
                    </dd>
                  </div>
                </div>
              )}
              {p.client_phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <dt className="text-xs text-gray-500">Phone</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {p.client_phone}
                    </dd>
                  </div>
                </div>
              )}
              {p.client_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <dt className="text-xs text-gray-500">Address</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {p.client_address}
                    </dd>
                  </div>
                </div>
              )}
            </dl>
          </div>

          {/* Budget Breakdown */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Budget
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Allocated</span>
                <span className="font-semibold">
                  {formatCurrency(p.budget_allocated || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Spent</span>
                <span className="font-semibold text-orange-600">
                  {formatCurrency(p.budget_spent || 0)}
                </span>
              </div>
              <div className="border-t pt-3 flex justify-between">
                <span className="text-gray-600">Remaining</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(
                    (p.budget_allocated || 0) - (p.budget_spent || 0),
                  )}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-orange-500 h-2 rounded-full"
                  style={{
                    width: `${Math.min(100, ((p.budget_spent || 0) / (p.budget_allocated || 1)) * 100)}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Risks/Issues */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Risks & Issues ({safeArray(p.risks).length})
              </h3>
              <button
                onClick={() => setShowRiskModal(true)}
                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> Add Risk
              </button>
            </div>
            {safeArray(p.risks).length > 0 ? (
              <div className="space-y-3">
                {safeArray(p.risks).map((risk, i) => (
                  <div
                    key={risk.id || i}
                    className={`p-3 rounded-lg border-l-4 ${risk.status === "resolved" ? "bg-green-50 border-green-500" : risk.impact === "high" ? "bg-red-50 border-red-500" : "bg-amber-50 border-amber-500"}`}
                  >
                    <div className="flex justify-between items-start">
                      <p className="text-sm font-medium text-gray-900 flex-1">
                        {risk.description}
                      </p>
                      <button
                        onClick={() => removeRiskMutation.mutate(i)}
                        className="text-gray-400 hover:text-red-500 ml-2"
                        title="Remove risk"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex gap-2 mt-1 text-xs text-gray-500">
                      <span>Impact: {risk.impact}</span>
                      <span>•</span>
                      <span>Probability: {risk.probability || "medium"}</span>
                      {risk.status && (
                        <>
                          <span>•</span>
                          <span>Status: {risk.status}</span>
                        </>
                      )}
                    </div>
                    {risk.mitigation && (
                      <p className="text-xs text-gray-600 mt-1">
                        Mitigation: {risk.mitigation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No risks recorded
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Risk Modal */}
      {showRiskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Register Risk/Issue</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={newRisk.description}
                  onChange={(e) =>
                    setNewRisk({ ...newRisk, description: e.target.value })
                  }
                  className="w-full border rounded-lg p-2"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Impact
                  </label>
                  <select
                    value={newRisk.impact}
                    onChange={(e) =>
                      setNewRisk({ ...newRisk, impact: e.target.value })
                    }
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Probability
                  </label>
                  <select
                    value={newRisk.probability}
                    onChange={(e) =>
                      setNewRisk({ ...newRisk, probability: e.target.value })
                    }
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mitigation Plan
                </label>
                <textarea
                  value={newRisk.mitigation}
                  onChange={(e) =>
                    setNewRisk({ ...newRisk, mitigation: e.target.value })
                  }
                  className="w-full border rounded-lg p-2"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowRiskModal(false)}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  newRisk.description && addRiskMutation.mutate(newRisk)
                }
                disabled={!newRisk.description || addRiskMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50"
              >
                {addRiskMutation.isLoading ? "Saving..." : "Register Risk"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Progress Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Update Project Progress
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={updateData.status}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setUpdateData({ ...updateData, status: newStatus });
                    if (newStatus === "completed") {
                      setShowUpdateModal(false);
                      setShowCompleteModal(true);
                    }
                  }}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Progress Percentage: {updateData.progress_percentage}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={updateData.progress_percentage}
                  onChange={(e) =>
                    setUpdateData({
                      ...updateData,
                      progress_percentage: parseInt(e.target.value),
                    })
                  }
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager Comment / Notes
                </label>
                <textarea
                  value={updateData.notes}
                  onChange={(e) =>
                    setUpdateData({ ...updateData, notes: e.target.value })
                  }
                  className="w-full border rounded-lg p-2"
                  rows={4}
                  placeholder="Add progress notes or comments..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowUpdateModal(false)}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => updateProjectMutation.mutate(updateData)}
                disabled={updateProjectMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {updateProjectMutation.isLoading ? "Saving..." : "Save Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Project Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="text-center mb-4">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Complete Project</h3>
              <p className="text-sm text-gray-500 mt-1">
                Mark this project as completed
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={makeTemplate}
                  onChange={(e) => setMakeTemplate(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="font-medium text-gray-900">
                    Make available as template
                  </span>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Allow other managers to use this project as a template for
                    new projects
                  </p>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setMakeTemplate(false);
                }}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteProject}
                disabled={updateProjectMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
              >
                {updateProjectMutation.isLoading
                  ? "Completing..."
                  : "Complete Project"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-96 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Project Comments</h3>
              <button
                onClick={() => setShowCommentModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {projectComments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No comments yet
                </p>
              ) : (
                projectComments.map((comment) => (
                  <div key={comment.id} className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary-700">
                          {comment.creatorName?.charAt(0)?.toUpperCase() || "U"}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-gray-700">
                        {comment.creatorName || "Unknown User"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {comment.timestamp ||
                          new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900">{comment.text}</p>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t space-y-2">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCommentModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={handleAddComment}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
